import React, { useState } from "react";

import { Modal } from "antd";

import StepCircle from "./StepCircle";
import UpdateFields from "./UpdateFields";
import UploadAnalysisExcel from "./UploadAnalysisExcel";
import UploadedFilesList from "./UploadedFilesList";

import { Specstype, Steps, StepStatus } from "./utils";

import "./UpdateOnboardingModal.css";

const INITIAL_FIELDS_STATE = {
  [Specstype.Product]: [],
  [Specstype.Environment]: [],
};

export default function UpdateOnboardingModal({ open, onClose }) {
  const [step, setStep] = useState(Steps.FILE_STATUS_TABLE); // 1: Fields update, 2: Upload analysis excel, 3: Spec data preview
  // eslint-disable-next-line
  const [stepOneStatus, setStepOneStatus] = useState(StepStatus.PROCESSING); // 0: waiting, 1: processing, 2: finsihed, 3: error
  const [stepTwoStatus, setStepTwoStatus] = useState(StepStatus.WAITING); // 0: waiting, 1: processing, 2: finsihed, 3: error
  const [stepThreeStatus, setStepThreeStatus] = useState(StepStatus.WAITING); // 0: waiting, 1: processing, 2: finsihed, 3: error
  const [selectedType, setSelectedType] = useState(Specstype.Product); // "product" | "environment"; Default: "product"
  const [fields, setFields] = useState(INITIAL_FIELDS_STATE);
  const [updatedLinkReport, setUpdatedLinkReport] = useState(null); // for product type, link report fields
  const [updatedLinkPattern, setUpdatedLinkPattern] = useState(null); // for environment type, link pattern
  const [updatedLinkPatternFields, setUpdatedLinkPatternFields] = useState(null); // for environment type, link pattern fields
  const [currentFile, setCurrentFile] = useState(null);

  const changeStep = (nextStep) => {
    const nextStepIndex = Object.values(Steps).indexOf(nextStep);
    [setStepOneStatus, setStepTwoStatus, setStepThreeStatus].forEach((setter, index) => {
      if (index < nextStepIndex) setter(StepStatus.FINISHED);
      else if (index > nextStepIndex) setter(StepStatus.WAITING);
      else setter(StepStatus.PROCESSING);
    });
    setStep(nextStep);
  };

  /**
 * Change the popup wording and set the current selected file
 * @param {*} type the confirmation popup type, either main-modal or file-modal
 * @param {*} status toggle the confirmation popup
 * @param {*} file the current selected file
 */
  const handleDeleteIconClick = (type, showConfirmation, file) => {
    setCurrentFile(file);
  };

  /** Convert the size of a file from bytes to proper unit
 * @param {Number} number size of a file
 * @return {String} file size to show on modal
 */
  const returnFileSize = (number) => {
    if (number < 1024) {
      return `${number} bytes`;
    } if (number >= 1024 && number < 1048576) {
      const numberInKB = number / 1024;
      return `${Math.round(numberInKB * 10) / 10} KB`; // round to 1 decimal place.
    } if (number >= 1048576) {
      const numberInMB = number / 1048576;
      return `${Math.round(numberInMB * 10) / 10} MB`; // round to 1 decimal place.
    }
    return "";
  };

  /**
   * Set selected type "product" or "environment"
   * @param {"product" | "environment"} type "product" | "enviroment"
   */
  const handleSelectedType = (type) => {
    setSelectedType(type);
  };

  const renderStepCircle = () => {
    const steps = [
      {
        key: 1,
        step: Steps.UPDATE_FIELDS,
        status: stepTwoStatus,
      },
      {
        key: 2,
        step: Steps.UPLOAD_SPEC_FILE,
        status: stepThreeStatus,
      },
    ];
    return steps.map((s, index) => (
      <React.Fragment key={s.key}>
        <StepCircle number={s.key} title={s.step} status={s.status} />
        {(index < steps.length - 1) && <div className={s.status !== StepStatus.FINISHED ? "UpdateOnboardingModal__StepWaiting" : "UpdateOnboardingModal__StepFinished"} />}
      </React.Fragment>
    ));
  };

  return (
    <Modal
      open={open}
      maskClosable={false}
      closable
      centered
      destroyOnClose
      footer={null}
      width="55vw"
      wrapClassName="UpdateOnboardingModal"
      onCancel={onClose}
    >
      <header
        className="UpdateOnboardingModal__Header"
        style={step === Steps.FILE_STATUS_TABLE ? { height: "auto" } : undefined}
      >
        <h2>Update Specs</h2>
        {step !== Steps.FILE_STATUS_TABLE && (
          <div className="UpdateOnboardingModal__steps-process">
            {renderStepCircle()}
          </div>
        )}
      </header>
      {step === Steps.FILE_STATUS_TABLE && (
        <UploadedFilesList
          onNext={() => {
            changeStep(Steps.UPDATE_FIELDS);
          }}
          onClose={onClose}
        />
      )}
      {step === Steps.UPDATE_FIELDS && (
        <UpdateFields
          proceedToUploadExcel={() => {
            changeStep(Steps.UPLOAD_SPEC_FILE);
          }}
          onClose={() => {
            changeStep(Steps.FILE_STATUS_TABLE);
          }}
          selectedType={selectedType}
          handleSelectedType={handleSelectedType}
          fields={fields}
          setFields={setFields}
          updatedLinkReport={updatedLinkReport}
          setUpdatedLinkReport={setUpdatedLinkReport}
          updatedLinkPattern={updatedLinkPattern}
          setUpdatedLinkPattern={setUpdatedLinkPattern}
          updatedLinkPatternFields={updatedLinkPatternFields}
          setUpdatedLinkPatternFields={setUpdatedLinkPatternFields}
        />
      )}
      {step === Steps.UPLOAD_SPEC_FILE && (
        <UploadAnalysisExcel
          handleDeleteIconClick={handleDeleteIconClick}
          currentFile={currentFile}
          returnFileSize={returnFileSize}
          proceedToDataPreview={() => {
            changeStep(Steps.FILE_STATUS_TABLE);
          }}
          backToUpdateFields={() => {
            changeStep(Steps.UPDATE_FIELDS);
          }}
          selectedType={selectedType}
          fields={fields}
          updatedLinkReport={updatedLinkReport}
          updatedLinkPatternFields={updatedLinkPatternFields}
        />
      )}
    </Modal>
  );
}
